import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWordsByUnitQuery } from '@entities/Word';
import { buildCramPath, buildUnitPath } from '@shared/config';
import { cn } from '@shared/lib';
import type { ModuleSummary } from '@shared/types';
import { Aurora, TargetSentence, Tooltip } from '@shared/ui';

interface ModulePreviewProps {
  module: ModuleSummary;
}

type Dir = 'en' | 'ru';

interface PreviewCard {
  key: string;
  en: string;
  ipa: string;
  ru: string;
  pos: string | null;
  def: string | null;
  ex: string | null;
  /** Простое предложение со словом в этом смысле: слово выделено, по нему видно,
   *  какой это перевод. */
  simple: string | null;
  manual: boolean;
}

const ChevronLeft = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRight = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const FlipIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3" />
    <path d="M21 3v5h-5M3 21v-5h5" />
  </svg>
);
const InfoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={className} aria-hidden>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 7.3v3.4M8 5.1v.05" />
  </svg>
);

/**
 * Режим ознакомления — полноэкранный иммерсивный просмотр карточек модуля
 * (листание word↔перевод, переключатель направления EN→RU / RU→EN, прогресс,
 * финальный экран). Готовит к практике: «К практике» ведёт в выбор cram-режима.
 * Маршрут верхнего уровня (без сайдбара), по образцу процесса заучивания.
 */
export function ModulePreview({ module: m }: ModulePreviewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useWordsByUnitQuery(m.id);

  const posShort = (p: string | null) => (p ? t(`moduleDetail.words.pos.${p}`, p) : null);

  // Карточки для листания = «готовые» слова: есть перевод и не требуют разбора.
  // Скрытые из модуля не листаем. Каждый выбранный перевод — своя карточка
  // (2026-09-25): у слова с «обмен» и «биржа» две карточки, каждая со своим простым
  // предложением. Без отмеченных смыслов — основной, как в тренировке.
  const cards = useMemo<PreviewCard[]>(() => {
    return (data ?? [])
      .filter((w) => w.ru && !w.isNeedAction && !w.isHidden)
      .flatMap((w) => {
        const meaning = w.meanings?.[0] ?? null;
        const regular = (meaning?.senses ?? []).filter((s) => !s.isRare && s.translations.length > 0);
        const ticked = new Set(w.sessionSenseIds ?? []);
        const chosen = regular.filter((s) => ticked.has(s.id));
        const senses = chosen.length > 0 ? chosen : regular.slice(0, 1);
        const base = { en: w.word, ipa: w.ipa, pos: meaning?.pos ?? null, manual: !!w.isManual };
        if (senses.length === 0) {
          return [{ ...base, key: `${w.stateId ?? w.id}`, ru: w.ru, def: null, ex: null, simple: null }];
        }
        return senses.map((sense) => ({
          ...base,
          key: `${w.stateId ?? w.id}:${sense.id}`,
          ru: sense.translations[0].text,
          def: sense.definition ?? null,
          ex: sense.exampleEn ?? null,
          simple: sense.simpleExampleEn ?? null,
        }));
      });
  }, [data]);

  const [dir, setDir] = useState<Dir>('en');
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  // Какая грань реально отрисована (меняется в середине анимации переворота,
  // когда карточка невидима) — отделено от `flipped`, который управляет фоном/подписью.
  const [showBack, setShowBack] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const swapTimer = useRef<number | null>(null);

  const total = cards.length;
  const done = total > 0 && idx >= total;
  const card = done ? null : (cards[idx] ?? null);

  const clearSwap = () => {
    if (swapTimer.current) {
      window.clearTimeout(swapTimer.current);
      swapTimer.current = null;
    }
  };

  const flip = () => {
    if (idx >= total) return;
    const nf = !flipped;
    setFlipped(nf);
    setAnimKey((k) => k + 1); // remount inner → перезапуск keyframe переворота
    clearSwap();
    swapTimer.current = window.setTimeout(() => setShowBack(nf), 190);
  };

  const goto = (n: number) => {
    clearSwap();
    setFlipped(false);
    setShowBack(false);
    setIdx(n);
  };
  const next = () => {
    if (idx >= total) return;
    goto(idx + 1);
  };
  const prev = () => {
    if (idx > 0) goto(idx - 1);
  };
  const changeDir = (d: Dir) => {
    if (d === dir) return;
    clearSwap();
    setFlipped(false);
    setShowBack(false);
    setDir(d);
  };
  const restart = () => goto(0);

  const toCram = () => navigate(buildCramPath(m.id));
  const back = () => navigate(buildUnitPath(m.id));

  // Клавиатура: Space — перевернуть, ←/→ — навигация. Слушатель вешаем один раз,
  // дёргая свежие хендлеры через ref (closures каждый рендер пересоздаются).
  const fns = useRef({ flip, next, prev });
  fns.current = { flip, next, prev };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        fns.current.flip();
      } else if (e.key === 'ArrowRight') {
        fns.current.next();
      } else if (e.key === 'ArrowLeft') {
        fns.current.prev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => () => clearSwap(), []);

  if (isLoading) {
    return (
      <div className="lx-prev-wrap">
        <Aurora />
        <div className="relative z-[2] flex min-h-[100dvh] items-center justify-center text-[14px] text-muted">
          {t('preview.loading')}
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="lx-prev-wrap">
        <Aurora />
        <div className="relative z-[2] mx-auto flex min-h-[100dvh] max-w-[760px] flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="font-serif text-[26px] font-medium tracking-[-0.01em]">{t('preview.empty.title')}</div>
          <div className="max-w-[420px] text-[14px] leading-relaxed text-muted">{t('preview.empty.sub')}</div>
          <button
            onClick={back}
            className="mt-1 inline-flex items-center gap-1.5 rounded-pill border border-line bg-card px-5 py-2.5 text-[14px] font-medium text-fg transition-colors hover:border-[#3a3a42]"
          >
            {t('preview.empty.back')}
          </button>
        </div>
      </div>
    );
  }

  const posLabel = card ? posShort(card.pos) : null;
  const cur = Math.min(idx + 1, total);
  const progress = done ? 100 : ((idx + 1) / total) * 100;

  const hint = flipped
    ? dir === 'en'
      ? t('preview.hint.backToWord')
      : t('preview.hint.backToTranslation')
    : dir === 'en'
      ? t('preview.hint.showTranslation')
      : t('preview.hint.showWord');
  const flipLabel = flipped
    ? t('preview.hide')
    : dir === 'en'
      ? t('preview.show.translation')
      : t('preview.show.word');

  const tagCls = 'text-[12px] font-semibold uppercase tracking-[0.18em]';
  const wordCls = 'text-balance text-center font-serif font-medium leading-[1.02] tracking-[-0.02em]';

  // b = отрисовать «заднюю» грань. Часть речи — на лицевой грани (!b), пример —
  // на задней (b), транскрипция — на стороне слова.
  const renderFace = (b: boolean) => {
    if (!card) return null;
    const wordSide = dir === 'en' ? !b : b;
    return (
      <>
        <div className={cn(tagCls, b ? 'text-accent-hi' : 'text-muted-2')}>
          {wordSide ? t('preview.tag.word') : t('preview.tag.translation')}
        </div>
        <div className={cn(wordCls, wordSide ? 'text-[44px] sm:text-[54px]' : 'text-[38px] sm:text-[46px]')}>
          {wordSide ? card.en : card.ru}
        </div>
        {wordSide && card.ipa && <div className="font-serif text-[17px] text-muted">{card.ipa}</div>}
        {wordSide && card.simple && (
          <div className="max-w-[420px] text-center font-serif text-[19px] leading-[1.45] text-fg/85" lang="en">
            <TargetSentence
              sentence={card.simple}
              word={card.en}
              markClassName="font-semibold text-fg underline decoration-accent decoration-[3px] underline-offset-[0.18em]"
            />
          </div>
        )}
        {!b && posLabel && (
          <div className="rounded-md border border-line bg-elev px-2.5 py-[3px] font-mono text-[12px] uppercase tracking-[0.08em] text-muted">
            {posLabel}
          </div>
        )}
        {b && card.def && (
          <div className="max-w-[440px] text-center text-[14px] leading-relaxed text-fg/70">{card.def}</div>
        )}
        {b && card.ex && (
          <div className="max-w-[400px] text-center font-serif text-[15px] italic leading-[1.5] text-muted">
            {card.ex}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="lx-prev-wrap">
      <Aurora />
      <div className="relative z-[2] mx-auto flex min-h-[100dvh] w-full max-w-[760px] flex-col px-5 pb-10 pt-6 sm:px-7">
        {/* top bar */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={back}
            className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors hover:text-fg"
          >
            ← {t('preview.back')}
          </button>
          <div className="hidden font-serif text-[15px] text-muted sm:block">
            {t('preview.kicker')} · <b className="font-medium text-fg">{m.title}</b>
          </div>
          <div className="inline-flex rounded-pill border border-line bg-elev p-[3px]">
            {(['en', 'ru'] as Dir[]).map((d) => (
              <button
                key={d}
                onClick={() => changeDir(d)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-pill px-3.5 py-2 text-[14px] font-medium transition-colors',
                  dir === d ? 'bg-card text-fg shadow-[0_1px_4px_rgba(0,0,0,0.4)]' : 'text-muted hover:text-fg',
                )}
              >
                {d === 'en' ? 'EN' : 'RU'} <span className="text-[12px] text-accent-hi">→</span> {d === 'en' ? 'RU' : 'EN'}
              </button>
            ))}
          </div>
        </div>

        {/* counter + progress */}
        <div className="mb-4 mt-5 flex items-center justify-between gap-3">
          <div className="text-[14px] text-muted">
            <b className="font-serif text-[16px] font-semibold italic text-fg">{cur}</b> {t('preview.of')} {total}
          </div>
          <div className="ml-4 h-1 max-w-[300px] flex-1 overflow-hidden rounded-pill bg-elev">
            <i
              className="block h-full rounded-pill bg-gradient-to-r from-accent to-accent-hi transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {done ? (
          <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
            <div className="mb-2 font-serif text-[30px] font-medium tracking-[-0.02em] sm:text-[34px]">
              {t('preview.done.title', { count: total })}
            </div>
            <div className="mb-6 text-sm text-muted">{t('preview.done.sub')}</div>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={restart}
                className="rounded-pill border border-line bg-card px-6 py-3.5 text-sm font-semibold text-fg transition-colors hover:border-[#3a3a42]"
              >
                {t('preview.done.again')}
              </button>
              <button
                onClick={toCram}
                className="rounded-pill bg-accent px-6 py-3.5 text-sm font-semibold text-[#1a120e] transition-transform hover:scale-[1.02]"
              >
                {t('preview.done.toCram')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-1 items-center justify-center py-2" style={{ minHeight: 340 }}>
              <div onClick={flip} className={cn('lx-prev-card', flipped && 'shown')}>
                {card?.manual && (
                  <div className="absolute right-3 top-3 z-10" onClick={(e) => e.stopPropagation()}>
                    <Tooltip side="bottom" text={t('preview.manual.note')}>
                      <button
                        type="button"
                        aria-label={t('preview.manual.aria')}
                        className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-elev/60 px-2.5 py-1 text-[12px] font-medium uppercase tracking-[0.08em] text-muted transition-colors hover:border-[#3a3a42] hover:text-fg"
                      >
                        <InfoIcon className="h-3 w-3" />
                        {t('preview.manual.badge')}
                      </button>
                    </Tooltip>
                  </div>
                )}
                <div key={animKey} className="lx-prev-anim flex w-full flex-col items-center gap-4">
                  {renderFace(showBack)}
                </div>
                <div className="absolute inset-x-0 bottom-[18px] px-4 text-center text-[12px] tracking-[0.08em] text-muted-2">
                  {hint}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-3.5">
              <button
                onClick={prev}
                disabled={idx === 0}
                aria-label={t('preview.prev')}
                className="inline-flex h-[54px] w-[54px] items-center justify-center rounded-full border border-line bg-card text-fg transition-transform enabled:hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="h-[22px] w-[22px]" />
              </button>
              <button
                onClick={flip}
                className="inline-flex h-[54px] max-w-[280px] flex-1 items-center justify-center gap-2.5 rounded-pill bg-accent text-[15px] font-semibold text-[#1a120e] shadow-[0_14px_30px_-12px_rgba(243,106,28,0.5)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <FlipIcon className="h-[18px] w-[18px]" />
                {flipLabel}
              </button>
              <button
                onClick={next}
                aria-label={t('preview.next')}
                className="inline-flex h-[54px] w-[54px] items-center justify-center rounded-full border border-line bg-card text-fg transition-transform hover:scale-105"
              >
                <ChevronRight className="h-[22px] w-[22px]" />
              </button>
            </div>

            <div className="mt-5 text-center text-[14px] text-muted">
              {t('preview.footnote')}{' '}
              <button onClick={toCram} className="font-semibold text-accent-hi transition-colors hover:text-accent">
                {t('preview.toCram')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
